import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { X, Copy, CheckCircle, Shield } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { createFirewallChangeRequest } from '@/lib/firewall'
import { supabase } from '@/lib/supabase'
import { Ship } from '@/types'
import { FirewallChangeRequestRow } from '@/types'
import { useTranslation } from 'react-i18next'

interface CreateFirewallChangeRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export const CreateFirewallChangeRequestModal: React.FC<CreateFirewallChangeRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { success, error } = useToast()
  const [ships, setShips] = useState<Ship[]>([])
  useEffect(() => {
    supabase.from('ships').select('*').order('vessel_name').then(({ data }) => { if (data) setShips(data) })
  }, [])
  const [loading, setLoading] = useState(false)
  const [submissionUrl, setSubmissionUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [formData, setFormData] = useState<Partial<FirewallChangeRequestRow>>({
    ship_id: '',
    request_date: new Date().toISOString().slice(0, 16),
    requester_department: '',
    requester_position: '',
    requester_name: ''
  })

  useEffect(() => {
    if (isOpen && ships.length > 0 && !formData.ship_id) {
      // pre-select the first ship if available
      setFormData(prev => ({ ...prev, ship_id: ships[0].id }))
    }
  }, [isOpen, ships])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.ship_id) {
      error('Error', 'Please select a vessel')
      return
    }

    setLoading(true)
    try {
      const result = await createFirewallChangeRequest({
        ...formData,
        request_date: formData.request_date ? new Date(formData.request_date).toISOString() : null
      })

      if (result.success && result.data) {
        if (result.submissionUrl) {
          setSubmissionUrl(result.submissionUrl)
          success('Success', 'Firewall change request created! Share the submission link with your customer.')
        } else {
          success('Success', 'Firewall change request created successfully')
          onSuccess()
          onClose()
          // reset form
          setFormData({
            ship_id: ships.length > 0 ? ships[0].id : '',
            request_date: new Date().toISOString().slice(0, 16),
            requester_department: '',
            requester_position: '',
            requester_name: ''
          })
        }
      } else {
        error('Error', result.error || 'Failed to create firewall change request')
      }
    } catch (err: any) {
      console.error('Error creating firewall change request:', err)
      error('Error', 'Failed to create firewall change request')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = () => {
    if (submissionUrl) {
      navigator.clipboard.writeText(submissionUrl)
      setCopied(true)
      success('Copied', 'Submission link copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleClose = () => {
    setSubmissionUrl(null)
    setCopied(false)
    setFormData({
      ship_id: ships.length > 0 ? ships[0].id : '',
      request_date: new Date().toISOString().slice(0, 16),
      requester_department: '',
      requester_position: '',
      requester_name: ''
    })
    onClose()
    onSuccess()
  }

  if (!isOpen) return null

  // show success screen with submission link
  if (submissionUrl) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              {t('firewall.submittedTitle')}
            </h2>
            <Button variant="outline" onClick={handleClose} className="p-2">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-slate-600 dark:text-slate-400">
              Your firewall change request has been created. Share this link with your customer to fill out the form:
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={submissionUrl}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button onClick={handleCopyLink} variant="outline">
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> This link will expire after the configured period. The customer can use it to fill out and submit the firewall change request form.
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleClose}>
                {t('common.finish')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('firewall.newRequest')}
          </h2>
          <Button variant="outline" onClick={handleClose} className="p-2">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('moc.vessel')} *
              </label>
              <select
                value={formData.ship_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, ship_id: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
              >
                <option value="">{t('moc.vessel')}</option>
                {ships.map(ship => (
                  <option key={ship.id} value={ship.id}>
                    {ship.vessel_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('firewall.requestDate')}
              </label>
              <Input
                type="datetime-local"
                value={formData.request_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Department
                </label>
                <Input
                  value={formData.requester_department || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                  placeholder="Department"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Position
                </label>
                <Input
                  value={formData.requester_position || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_position: e.target.value }))}
                  placeholder="Position"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Name
                </label>
                <Input
                  value={formData.requester_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                  placeholder="Name"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('adminPages.loading') : t('firewall.newRequest')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

